import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
  AuthService,
  UserActions as CxUserActions,
  UserConsentService as CxUserConsentService,
  GIVE_CONSENT_PROCESS_ID,
  OCC_USER_ID_ANONYMOUS,
  StateWithProcess,
  WITHDRAW_CONSENT_PROCESS_ID,
} from '@spartacus/core';
import { Observable, iif } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ConsentsConfig } from '../../anonymous-consents/config/consents-config';
import { AnonymousConsentsService } from '../../anonymous-consents/facade';
import { UserIdService } from '../../auth/user-auth/facade/user-id.service';
import { LoaderError } from '../../model';
import { Consent, ConsentTemplate } from '../../model/consent.model';
import {
  getProcessErrorFactory,
  getProcessLoadingFactory,
  getProcessSuccessFactory,
} from '../../process/store/selectors/process.selectors';
import { UserActions } from '../store/actions/index';
import { UserConsentsSelectors } from '../store/selectors/user.selectors';
import { StateWithUser } from '../store/user-state';
import { UserUtilService } from './user-util.service';

@Injectable()
export class UserConsentService extends CxUserConsentService {
  constructor(
    protected store: Store<StateWithUser & StateWithProcess<void>>,
    protected authService: AuthService,
    protected userIdService: UserIdService,
    private anonymousConsentsService: AnonymousConsentsService,
    private config: ConsentsConfig,
    private userUtilService: UserUtilService
  ) {
    super(store, authService, userIdService);
  }

  /**
   * Retrieves all consents.
   */
  loadConsents(): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new CxUserActions.LoadUserConsents(userId)),
      () => {}
    );
  }

  /**
   * Returns all consent templates. If `loadIfMissing` parameter is set to `true`, the method triggers the load if consent templates.
   * @param loadIfMissing is set to `true`, the method will load templates if those are not already present. The default value is `false`.
   */
  getConsents(loadIfMissing = false): Observable<ConsentTemplate[]> {
    return iif(
      () => loadIfMissing,
      this.store.pipe(
        select(UserConsentsSelectors.getConsentsValue),
        withLatestFrom(this.getConsentsResultLoading(), this.getConsentsResultSuccess()),
        filter(([_templates, loading, _success]) => !loading),
        tap(([templates, _loading, success]) => {
          if (!templates || templates.length === 0) {
            // avoid infinite loop - if we've already attempted to load templates and we got an empty array as the response
            if (!success) {
              this.loadConsents();
            }
          }
        }),
        filter(([templates, _loading]) => Boolean(templates)),
        map(([templates, _loading]) => templates),
        map((templates) => templates.filter((template) => this.config.consents.user.consents.includes(template.id)))
      ),
      this.store.pipe(
        select(UserConsentsSelectors.getConsentsValue),
        map((templates) => templates && templates.filter((template) => this.config.consents.user.consents.includes(template.id)))
      )
    );
  }

  /**
   * Get Required consents that are not given by user but needs approval
   */
  getRequiredConsentsNotGiven(): Observable<Consent[]> {
    return this.store.pipe(
      select(UserConsentsSelectors.getConsentsNotGivenState),
      filter((loaderState) => !loaderState.loading && !!loaderState.value),
      map((loaderState) => loaderState.value)
    );
  }

  getOptionalConsents(): Observable<ConsentTemplate[]> {
    return this.getConsents(true).pipe(
      map((templates) => this.filterConsentTemplates(templates, this.userUtilService.getHiddenLoginConsents()))
    );
  }

  /**
   * Returns the consents loading flag
   */
  getConsentsResultLoading(): Observable<boolean> {
    return this.store.pipe(select(UserConsentsSelectors.getConsentsLoading));
  }

  /**
   * Returns the consents success flag
   */
  getConsentsResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(UserConsentsSelectors.getConsentsSuccess));
  }

  /**
   * Returns the consents error details
   */
  getConsentsResultErrorDetails(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(UserConsentsSelectors.getConsentsErrorDetails));
  }

  /**
   * Returns the registered consent for the given template ID.
   *
   * As a side-effect, the method will call `getConsents(true)` to load the templates if those are not present.
   *
   * @param templateId a template ID by which to filter the registered templates.
   */
  getConsent(templateId: string): Observable<Consent | undefined> {
    return this.authService.isUserLoggedIn().pipe(
      tap(() => this.getConsents(true)),
      switchMap(() => this.store.pipe(select(UserConsentsSelectors.getConsentByTemplateId(templateId)))),
      withLatestFrom(this.getGiveConsentResultLoading()),
      filter(([template, loading]) => !!template && !loading),
      map(([template, _loading]) => {
        return { ...template?.currentConsent, templateId: template?.id };
      })
    );
  }

  /**
   * Give consent for specified consent template ID and version.
   * @param consentTemplateId a template ID for which to give a consent
   * @param consentTemplateVersion a template version for which to give a consent
   */
  giveConsent(consentTemplateId: string, consentTemplateVersion: number): void {
    if (this.config.consents.user.consents.includes(consentTemplateId)) {
      this.userIdService.takeUserId(true).subscribe(
        (userId) =>
          this.store.dispatch(
            new CxUserActions.GiveUserConsent({
              userId,
              consentTemplateId: consentTemplateId,
              consentTemplateVersion,
            })
          ),
        () => {}
      );
    }
  }

  /**
   * Returns the give consent process loading flag
   */
  getGiveConsentResultLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(GIVE_CONSENT_PROCESS_ID)));
  }

  /**
   * Returns the give consent process success flag
   */
  getGiveConsentResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(GIVE_CONSENT_PROCESS_ID)));
  }

  /**
   * Returns the give consent process error details
   */
  getGiveConsentResultErrorDetails(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(GIVE_CONSENT_PROCESS_ID)));
  }

  /**
   * Withdraw consent for the given `consentCode`
   * @param consentCode for which to withdraw the consent
   */
  withdrawConsent(consentCode: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) =>
        this.store.dispatch(
          new CxUserActions.WithdrawUserConsent({
            userId,
            consentCode,
          })
        ),
      () => {}
    );
  }

  denyConsent(consentTemplateId: string, consentTemplateVersion: number): void {
    if (this.config.consents.user.consents.includes(consentTemplateId)) {
      this.userIdService.takeUserId(true).subscribe(
        (userId) =>
          this.store.dispatch(
            new UserActions.DenyUserConsent({
              userId,
              consentTemplateId: consentTemplateId,
              consentTemplateVersion,
            })
          ),
        () => {}
      );
    }
  }

  /**
   * Returns the withdraw consent process loading flag
   */
  getWithdrawConsentResultLoading(): Observable<boolean> {
    return this.store.pipe(select(getProcessLoadingFactory(WITHDRAW_CONSENT_PROCESS_ID)));
  }

  /**
   * Returns the withdraw consent process success flag
   */
  getWithdrawConsentResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(getProcessSuccessFactory(WITHDRAW_CONSENT_PROCESS_ID)));
  }

  /**
   * Returns the withdraw consent process error flag
   */
  getWithdrawConsentResultErrorDetails(): Observable<LoaderError | undefined> {
    return this.store.pipe(select(getProcessErrorFactory(WITHDRAW_CONSENT_PROCESS_ID)));
  }

  isTrackingAllowed(): Observable<boolean> {
    return this.checkIfConsentIsGiven(this.config.consents.user.trackingConsent, this.config.consents.anonymous.trackingConsent);
  }

  isPersonalizationAllowed(): Observable<boolean> {
    return this.checkIfConsentIsGiven(
      this.config.consents.user.personalizationConsent,
      this.config.consents.anonymous.personalizationConsent
    );
  }

  isMarketingThirdPartyAllowed(): Observable<boolean> {
    return this.checkIfConsentIsGiven(
      this.config.consents.user.marketingThirdPartyConsent,
      this.config.consents.anonymous.marketingThirdPartyConsent
    );
  }

  private checkIfConsentIsGiven(consentTemplateCode: string, anonymousConsentTemplateCode: string): Observable<boolean> {
    return this.userIdService.getUserId().pipe(
      switchMap((userId) => {
        if (OCC_USER_ID_ANONYMOUS === userId) {
          return this.anonymousConsentsService
            .getConsent(anonymousConsentTemplateCode)
            .pipe(map((anonConsent) => this.anonymousConsentsService.isConsentGiven(anonConsent)));
        }
        return this.getConsent(consentTemplateCode).pipe(map((consent) => this.isConsentGiven(consent)));
      })
    );
  }

  isBannerVisible(): Observable<boolean> {
    return this.store.pipe(select(UserConsentsSelectors.getConsentsBanner));
  }

  isSettingsVisible(): Observable<boolean> {
    return this.store.pipe(select(UserConsentsSelectors.getConsentsSettingsVisible));
  }

  toggleBannerVisible(dismissed: boolean): void {
    this.store.dispatch(new UserActions.SetConsentsBannerVisible(dismissed));
  }

  toggleSettingsModalVisible(visible: boolean, showCloseButton?: boolean): void {
    this.store.dispatch(new UserActions.SetConsentsSettingsModalVisible(visible, visible ? showCloseButton : false));
  }
}
